"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

export interface StepBreakdownProposal {
  goalId: string;
  goalTitle: string;
  parentStep: string;
  reason: string;
  subSteps: string[];
}

interface Props {
  open: boolean;
  proposals: StepBreakdownProposal[];
  onClose: () => void;
  onConfirm: (
    accepted: Array<{
      goalId: string;
      parentStep: string;
      subSteps: string[];
    }>,
  ) => Promise<void> | void;
}

// Phase 4 — confirm dialog shown after AI judge says one or more
// of the user's selected goal next-steps are too big to schedule
// directly. The user gets to:
//   - Edit each proposed sub-step inline (LLM output isn't sacred)
//   - Toggle off goals they don't want broken down (we'll just
//     schedule the original step instead)
//   - Confirm → parent triggers milestone update + re-schedules
//
// The component owns its own draft state because the LLM proposal
// is the starting point but the final shape of the breakdown is
// the user's. On Cancel we drop everything.
export default function StepBreakdownDialog({
  open,
  proposals,
  onClose,
  onConfirm,
}: Props) {
  const [drafts, setDrafts] = useState<
    Record<string, { subSteps: string[]; accepted: boolean }>
  >({});
  const [confirming, setConfirming] = useState(false);

  // Re-seed drafts whenever the dialog opens with a new set of
  // proposals — but never mid-flight (the user may already be
  // typing in the inputs).
  useEffect(() => {
    if (!open) return;
    setDrafts(
      Object.fromEntries(
        proposals.map((p) => [
          p.goalId,
          { subSteps: [...p.subSteps], accepted: true },
        ]),
      ),
    );
  }, [open, proposals]);

  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const updateSubStep = (goalId: string, index: number, value: string) => {
    setDrafts((current) => {
      const draft = current[goalId];
      if (!draft) return current;
      const next = [...draft.subSteps];
      next[index] = value;
      return { ...current, [goalId]: { ...draft, subSteps: next } };
    });
  };

  const removeSubStep = (goalId: string, index: number) => {
    setDrafts((current) => {
      const draft = current[goalId];
      if (!draft) return current;
      return {
        ...current,
        [goalId]: {
          ...draft,
          subSteps: draft.subSteps.filter((_, i) => i !== index),
        },
      };
    });
  };

  const addSubStep = (goalId: string) => {
    setDrafts((current) => {
      const draft = current[goalId];
      if (!draft) return current;
      return {
        ...current,
        [goalId]: { ...draft, subSteps: [...draft.subSteps, ""] },
      };
    });
  };

  const toggleAccepted = (goalId: string) => {
    setDrafts((current) => {
      const draft = current[goalId];
      if (!draft) return current;
      return {
        ...current,
        [goalId]: { ...draft, accepted: !draft.accepted },
      };
    });
  };

  // UAT 2026-05-25 bug #2: the <2 filter silently drops entries with
  // too few valid sub-steps, which left the user staring at a Confirm
  // button that did nothing visible. Compute eligibility up front so
  // the UI can (a) disable the button when no entry would actually
  // be applied, and (b) surface a clear hint about why.
  const eligibleCount = proposals.reduce((count, p) => {
    const draft = drafts[p.goalId];
    if (!draft?.accepted) return count;
    const valid = draft.subSteps
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    return valid.length >= 2 ? count + 1 : count;
  }, 0);
  // Allow the empty-accepted path (user toggled everything to "Skip")
  // — the parent has a sensible branch for that. Only block when the
  // user has accepted entries but they don't pass the 2+ sub-step
  // threshold (which is the bug that previously appeared as "no
  // response").
  const acceptedCount = proposals.filter(
    (p) => drafts[p.goalId]?.accepted
  ).length;
  const hasInvalidAccepted = acceptedCount > 0 && eligibleCount === 0;

  const handleConfirm = async () => {
    if (confirming) return;
    if (hasInvalidAccepted) return;
    setConfirming(true);
    try {
      const accepted = proposals
        .filter((p) => drafts[p.goalId]?.accepted)
        .map((p) => ({
          goalId: p.goalId,
          parentStep: p.parentStep,
          subSteps: drafts[p.goalId]
            .subSteps.map((s) => s.trim())
            .filter((s) => s.length > 0),
        }))
        .filter((entry) => entry.subSteps.length >= 2);
      await onConfirm(accepted);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[120] flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, backdropFilter: "blur(3px)" }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
          style={{ backgroundColor: "rgba(40,28,18,0.32)" }}
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-labelledby="step-breakdown-title"
        >
          <motion.div
            className="relative max-h-[85vh] w-full max-w-lg overflow-hidden rounded-[14px] bg-[#FFF8F0] shadow-2xl"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ duration: 0.36, ease: [0.2, 0.8, 0.2, 1] }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-[rgba(80,60,40,0.10)] px-7 pt-[22px] pb-4">
              <p
                className="text-[10.5px] font-bold uppercase"
                style={{ color: "#C97A3D", letterSpacing: "1.4px" }}
              >
                AI suggestion
              </p>
              <h3
                id="step-breakdown-title"
                className="mt-1 font-serif text-[22px] font-medium tracking-[-0.3px] text-[#2B2620]"
              >
                Break {proposals.length === 1 ? "this step" : "these steps"} down first?
              </h3>
              <p className="mt-2 text-[13px] leading-relaxed text-[#5C544A]">
                {proposals.length === 1
                  ? "The next step looks too broad to fit one focused session. Here's a smaller breakdown — edit if needed."
                  : `${proposals.length} of your queued goals have steps that look too broad. Adjust each breakdown if needed.`}
              </p>
              {/* UAT 2026-05-25 bug #2: explicit instruction so the
                  user knows exactly what to do — Confirm to save these
                  sub-steps, Skip to keep the original step as-is, or
                  Cancel to bail. Previously the dialog opened cold
                  with no guidance and the user said "confusing — how
                  to breakdown that?". */}
              <p className="mt-2 text-[11.5px] leading-relaxed text-[#7A6F5E]">
                Each goal needs <span className="font-semibold">at least 2 sub-steps</span> to apply.
                Edit a row, mark it <span className="font-semibold">Skip</span> to leave
                the original step alone, or <span className="font-semibold">Cancel</span> to back out.
              </p>
            </div>

            <div className="max-h-[58vh] overflow-y-auto px-7 py-5">
              <div className="space-y-5">
                {proposals.map((proposal) => {
                  const draft = drafts[proposal.goalId];
                  if (!draft) return null;
                  return (
                    <section
                      key={proposal.goalId}
                      className={`rounded-xl border px-4 py-3 transition-opacity ${
                        draft.accepted
                          ? "border-forest/20 bg-white"
                          : "border-border/70 bg-bg/40 opacity-50"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted">
                            {proposal.goalTitle}
                          </p>
                          <p className="mt-1 text-sm font-semibold text-text">
                            {proposal.parentStep}
                          </p>
                          <p className="mt-1 text-[11px] leading-relaxed text-text-light">
                            {proposal.reason}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleAccepted(proposal.goalId)}
                          className={`shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.12em] transition-colors ${
                            draft.accepted
                              ? "bg-forest/10 text-forest-med hover:bg-forest/15"
                              : "border border-border bg-white text-text-light hover:bg-bg"
                          }`}
                        >
                          {draft.accepted ? "Will break down" : "Skip"}
                        </button>
                      </div>

                      {draft.accepted && (
                        <div className="mt-3 space-y-2">
                          {draft.subSteps.map((sub, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-2 rounded-lg border border-border/60 bg-bg/60 px-2.5 py-1.5"
                            >
                              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-forest/10 text-[10px] font-semibold text-forest-med">
                                {index + 1}
                              </span>
                              <input
                                type="text"
                                value={sub}
                                onChange={(event) =>
                                  updateSubStep(
                                    proposal.goalId,
                                    index,
                                    event.target.value,
                                  )
                                }
                                placeholder="Sub-step…"
                                className="min-w-0 flex-1 border-0 bg-transparent text-[13px] text-text placeholder-muted focus:outline-none"
                              />
                              <button
                                type="button"
                                onClick={() => removeSubStep(proposal.goalId, index)}
                                aria-label="Remove sub-step"
                                className="flex h-6 w-6 items-center justify-center rounded-full text-muted transition-colors hover:bg-rose/10 hover:text-rose"
                              >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                  <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => addSubStep(proposal.goalId)}
                            className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border/70 bg-bg/30 px-3 py-1.5 text-[11px] font-medium text-text-light transition-colors hover:border-forest/40 hover:bg-sage-pale/40 hover:text-forest"
                          >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M12 5v14M5 12h14" />
                            </svg>
                            Add sub-step
                          </button>
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-[rgba(80,60,40,0.10)] px-7 py-4">
              <p className="text-[11px] leading-relaxed text-text-light">
                Sub-steps will be added under each goal&apos;s milestone.
              </p>
              <div className="flex items-center gap-2">
                {hasInvalidAccepted && (
                  <span
                    className="text-[10.5px] font-medium text-[#C97A3D]"
                    role="alert"
                  >
                    Need at least 2 sub-steps per accepted goal
                  </span>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  disabled={confirming}
                  className="rounded-full border border-border px-4 py-1.5 text-[12px] font-semibold text-text-light transition-colors hover:bg-secondary disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleConfirm()}
                  disabled={confirming || hasInvalidAccepted}
                  title={
                    hasInvalidAccepted
                      ? "Add at least 2 sub-steps per accepted goal, or mark them as Skip."
                      : undefined
                  }
                  className="rounded-full bg-forest-med px-5 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-forest disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {confirming ? "Confirming…" : "Confirm & schedule"}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
